import React, { useEffect, useRef, useState } from "react";
import { site } from "../data/site";
import { useLang, pick } from "../i18n/LanguageContext";
import { t } from "../i18n/translations";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

// Kevyet tarkistukset: osoitteessa on @ ja piste, numerossa vain puhelinnumeron
// merkkejä ja vähintään kuusi numeroa. Tarkoitus on napata kirjoitusvirheet, ei
// torjua epätavallisia mutta toimivia osoitteita.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s().-]+$/;

// Palauttaa virheet käännösavaimina, jotta kielen vaihto päivittää ne.
const validate = (f) => {
  const errors = {};
  const email = f.email.trim();
  const phone = f.phone.trim();
  if (!f.name.trim()) errors.name = "formErrorName";
  if (!email && !phone) errors.contact = "formErrorContact";
  if (email && !EMAIL_RE.test(email)) errors.email = "formErrorEmail";
  if (phone && (!PHONE_RE.test(phone) || phone.replace(/\D/g, "").length < 6))
    errors.phone = "formErrorPhone";
  return errors;
};

// Lähetysyrityksessä fokus siirtyy ensimmäiseen virheelliseen kenttään.
const FOCUS_ORDER = [
  ["name", "name"],
  ["contact", "email"],
  ["email", "email"],
  ["phone", "phone"],
];

const Yhteys = () => {
  const { lang } = useLang();
  const tr = pick(lang);

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [sentCallback, setSentCallback] = useState(false);
  const formRef = useRef(null);
  const sentRef = useRef(null);

  // Kiitostila korvaa lomakkeen, joten fokus siirretään sen otsikkoon.
  useEffect(() => {
    if (status === "sent" && sentRef.current) sentRef.current.focus();
  }, [status]);

  // Virheet näytetään vasta lähetyksessä. Korjattu virhe poistuu heti
  // kirjoittaessa, mutta uusia ei lisätä ennen seuraavaa lähetysyritystä.
  const onChange = (e) => {
    const next = { ...form, [e.target.name]: e.target.value };
    setForm(next);
    if (Object.keys(errors).length) {
      const now = validate(next);
      setErrors((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => now[key]))
      );
    }
  };

  // Lomake lähetetään Netlify Formsiin (ks. piilotettu lomake public/index.html).
  // Netlify ottaa POSTin vastaan sivun omassa osoitteessa ja lähettää ilmoituksen
  // hallintapaneelissa asetettuun sähköpostiin. Tyhjä viesti on soittopyyntö.
  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) {
      setStatus("idle");
      setAttempt((n) => n + 1);
      const [, field] = FOCUS_ORDER.find(([key]) => found[key]);
      formRef.current.elements.namedItem(field).focus();
      return;
    }
    const data = e.target;
    const message = form.message.trim();
    const body = new URLSearchParams({
      "form-name": data.getAttribute("name"),
      "bot-field": data["bot-field"] ? data["bot-field"].value : "",
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: message || tr(t.yhteys.formCallbackSubject),
    }).toString();
    setStatus("sending");
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSentCallback(!message && Boolean(form.phone.trim()));
      setStatus("sent");
      setForm(EMPTY);
    } catch (err) {
      // Kentät jäävät ennalleen, jotta teksti ei katoa.
      setStatus("error");
    }
  };

  // WhatsApp-linkki: numero ilman välilyöntejä ja plussaa. Esitäyttöön vain nimi
  // ja viesti, ei yhteystietoja. Tyhjällä lomakkeella lähtee tervehdys.
  const waNumber = site.contact.whatsapp.replace(/\D/g, "");
  const waLink = (text) => `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
  const waLines = [form.name.trim(), form.message.trim()].filter(Boolean);
  const waUrl = waLink(waLines.length ? waLines.join("\n") : tr(t.yhteys.whatsappGreeting));
  const waPlainUrl = waLink(tr(t.yhteys.whatsappGreeting));

  // Varakanavat virhetilassa: sähköposti esitäytettynä aiheella ja viestillä.
  const mailParams = [`subject=${encodeURIComponent(tr(t.yhteys.formMailSubject))}`];
  if (form.message.trim()) mailParams.push(`body=${encodeURIComponent(form.message.trim())}`);
  const mailUrl = `mailto:${site.contact.email}?${mailParams.join("&")}`;
  const telUrl = `tel:${site.contact.phone.replace(/\s/g, "")}`;

  // Kentän virheteksti ja aria-kuvaukset. Ohje "toinen riittää" kuvaa molempia
  // yhteystietokenttiä, ja ryhmän virhe merkitsee kummankin virheelliseksi.
  const errorText = (key, id) =>
    errors[key] ? (
      <p id={id} className="yhteys-form__error">
        {tr(t.yhteys[errors[key]])}
      </p>
    ) : null;
  const describe = (...ids) => ids.filter(Boolean).join(" ") || undefined;
  const contactIds = ["yhteys-contact-hint", errors.contact && "yhteys-contact-error"];
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <article className="page page--yhteys">
      <div className="page__inner">
        <header className="page__header">
          <h1 className="page__title">{tr(t.yhteys.titleSimple)}</h1>
          <p className="page__intro">{tr(t.yhteys.lead)}</p>
        </header>

        <section className="page__section yhteys__layout">
          {status === "sent" ? (
            <div className="yhteys-form yhteys-form--sent">
              <h2 className="yhteys-form__sent-title" ref={sentRef} tabIndex={-1}>
                {tr(t.yhteys.formSentTitle)}
              </h2>
              <p className="yhteys-form__sent-text">
                {tr(sentCallback ? t.yhteys.formSentCallback : t.yhteys.formSent)}
              </p>
            </div>
          ) : (
            <form
              ref={formRef}
              className="yhteys-form"
              name="yhteys"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              aria-label={tr(t.yhteys.formAria)}
              onSubmit={onSubmit}
              noValidate
            >
              <input type="hidden" name="form-name" value="yhteys" />
              {/* Honeypot — piilossa ihmisiltä, botit täyttävät */}
              <p className="yhteys-form__honeypot" aria-hidden="true">
                <label>
                  Älä täytä tätä kenttää: <input name="bot-field" tabIndex={-1} autoComplete="off" />
                </label>
              </p>

              <div className="yhteys-form__field">
                <label className="yhteys-form__label" htmlFor="yhteys-name">
                  {tr(t.yhteys.formNameLabel)}
                </label>
                <input
                  id="yhteys-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  autoComplete="name"
                  aria-invalid={errors.name ? true : undefined}
                  aria-describedby={describe(errors.name && "yhteys-name-error")}
                  className="yhteys-form__input"
                />
                {errorText("name", "yhteys-name-error")}
              </div>

              <fieldset className="yhteys-form__group">
                <legend className="yhteys-form__label">{tr(t.yhteys.formContactLegend)}</legend>
                <p id="yhteys-contact-hint" className="yhteys-form__hint">
                  {tr(t.yhteys.formContactHint)}
                </p>

                <div className="yhteys-form__row">
                  <div className="yhteys-form__field">
                    <label className="yhteys-form__label" htmlFor="yhteys-email">
                      {tr(t.yhteys.formEmailLabel)}
                    </label>
                    <input
                      id="yhteys-email"
                      type="email"
                      name="email"
                      inputMode="email"
                      value={form.email}
                      onChange={onChange}
                      autoComplete="email"
                      autoCapitalize="off"
                      spellCheck={false}
                      aria-invalid={errors.contact || errors.email ? true : undefined}
                      aria-describedby={describe(...contactIds, errors.email && "yhteys-email-error")}
                      className="yhteys-form__input"
                    />
                    {errorText("email", "yhteys-email-error")}
                  </div>

                  <div className="yhteys-form__field">
                    <label className="yhteys-form__label" htmlFor="yhteys-phone">
                      {tr(t.yhteys.formPhoneLabel)}
                    </label>
                    <input
                      id="yhteys-phone"
                      type="tel"
                      name="phone"
                      inputMode="tel"
                      value={form.phone}
                      onChange={onChange}
                      autoComplete="tel"
                      aria-invalid={errors.contact || errors.phone ? true : undefined}
                      aria-describedby={describe(...contactIds, errors.phone && "yhteys-phone-error")}
                      className="yhteys-form__input"
                    />
                    {errorText("phone", "yhteys-phone-error")}
                  </div>
                </div>
                {errorText("contact", "yhteys-contact-error")}
              </fieldset>

              <div className="yhteys-form__field">
                <label className="yhteys-form__label" htmlFor="yhteys-message">
                  {tr(t.yhteys.formMessageLabel)}
                </label>
                <textarea
                  id="yhteys-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  placeholder={tr(t.yhteys.formMessagePlaceholder)}
                  className="yhteys-form__input yhteys-form__textarea"
                />
              </div>

              <div className="yhteys-form__footer">
                {/* Ei disabled-attribuuttia: nappi pitää fokuksen lähetyksen ajan,
                    tuplalähetys estetään onSubmitissa. */}
                <button
                  type="submit"
                  className="yhteys-form__submit"
                  aria-disabled={status === "sending" ? true : undefined}
                >
                  {status === "sending" ? tr(t.yhteys.formSending) : tr(t.yhteys.formSubmit)}
                </button>
                <a
                  className="yhteys-form__alt"
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {tr(t.yhteys.formOrWhatsapp)}{" "}
                  <span className="yhteys-form__alt-brand">{tr(t.yhteys.formOrWhatsappBrand)}</span>
                </a>
              </div>

              {/* Ruudunlukija kuulee yhteenvedon, fokus on jo ensimmäisessä virheessä.
                  Avain vaihtuu joka yrityksellä, jotta sama teksti luetaan uudelleen. */}
              {hasErrors && (
                <p key={attempt} className="yhteys-form__summary" role="alert">
                  {tr(t.yhteys.formErrorSummary)}
                </p>
              )}

              {status === "error" && (
                <div className="yhteys-form__status yhteys-form__status--error" role="alert">
                  <p className="yhteys-form__status-text">{tr(t.yhteys.formError)}</p>
                  <ul className="yhteys-form__fallback">
                    <li>
                      <a className="yhteys-form__fallback-link" href={mailUrl}>
                        <span className="yhteys-form__fallback-label">{tr(t.yhteys.write)}</span>
                        {site.contact.email}
                      </a>
                    </li>
                    <li>
                      <a className="yhteys-form__fallback-link" href={telUrl}>
                        <span className="yhteys-form__fallback-label">{tr(t.yhteys.call)}</span>
                        {site.contact.phone}
                      </a>
                    </li>
                    <li>
                      <a
                        className="yhteys-form__fallback-link"
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="yhteys-form__fallback-label">{tr(t.yhteys.whatsapp)}</span>
                        {site.contact.whatsapp}
                      </a>
                    </li>
                  </ul>
                  <p className="yhteys-form__status-text">{tr(t.yhteys.formErrorKept)}</p>
                </div>
              )}
            </form>
          )}

          <aside className="yhteys-aside" aria-label={tr(t.yhteys.contactInfoLabel)}>
            <div className="yhteys-aside__col">
              <span className="yhteys-aside__label">{tr(t.yhteys.write)}</span>
              <a className="yhteys-aside__value" href={`mailto:${site.contact.email}`}>
                {site.contact.email}
              </a>
            </div>

            <div className="yhteys-aside__col">
              <span className="yhteys-aside__label">{tr(t.yhteys.call)}</span>
              <a className="yhteys-aside__value" href={telUrl}>
                {site.contact.phone}
              </a>
            </div>

            <div className="yhteys-aside__col">
              <span className="yhteys-aside__label">{tr(t.yhteys.whatsapp)}</span>
              <a
                className="yhteys-aside__value"
                href={waPlainUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {site.contact.whatsapp}
              </a>
            </div>

            <div className="yhteys-aside__col">
              <span className="yhteys-aside__label">{tr(t.yhteys.visit)}</span>
              <address className="yhteys-aside__value yhteys-aside__address">
                {site.contact.address.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </address>
            </div>
          </aside>
        </section>
      </div>
    </article>
  );
};

export default Yhteys;
